import { useRoute, reactive, computed, ref, defineComponent, getCurrentInstance, useContext, useCssModule, } from '@nuxtjs/composition-api';
import { useTask } from 'vue-concurrency';
import { createHead, useAsyncRef } from '~/utils';
import { createGridChunks, getArticlesSize } from '~/composables';
import { presets } from '~/composables/grid/club';
import { useDuck } from '~/services';
import { SECTIONS } from '~/constants';
import { CLUB_PROMO } from '~/pages/club/data';
/**
 * Duck service.
 */
const duck = useDuck();
/**
 * Columns
 */
export const columns = {
    desktop: 2,
    desktopMedium: 2,
    desktopWide: 3,
    mobile: 1,
    mobileMedium: 1,
    mobileWide: 1,
    tablet: 2,
};
export default defineComponent({
    name: 'PageClubPartners',
    setup() {
        /**
         * Composables.
         */
        const context = useContext();
        const css = useCssModule();
        const route = useRoute();
        const viewport = context.$viewport;
        /**
         * Instance.
         */
        const instance = getCurrentInstance();
        /**
         * Articles.
         */
        const articles = useAsyncRef(instance, 'articles');
        const isDone = useAsyncRef(instance, 'isDone');
        const currentPartner = useAsyncRef(instance, 'currentPartner');
        const tabsList = useAsyncRef(instance, 'tabsList');
        const currentTab = useAsyncRef(instance, 'currentTab');
        const currentFilter = ref('');
        const foundPartner = ref('');
        const isSearchFieldHasError = ref(false);
        const isFoundArticles = ref(false);
        /**
         * Current preset.
         */
        const articlesCards = computed(() => getArticlesSize(viewport.breakpoint, {
            columns,
            presets,
        }));
        /**
         * Grids.
         */
        const grids = computed(() => createGridChunks(articles.value, articlesCards.value));
        const pageOffset = useAsyncRef(instance, 'pageOffset');
        const tabs = reactive({
            currentTab,
            tabs: computed(() => {
                return tabsList.value?.map((tab) => {
                    return {
                        ...tab,
                        theme: tab.value === route.value.path.replace(/^\/|\/$/g, '') ? 'yellow' : 'lighter',
                    };
                });
            }) || [],
            change(tab) {
                const { theme = '', title, to, value } = tab;
                tabs.currentTab = {
                    theme,
                    title,
                    to,
                    value,
                };
            },
        });
        /**
         * Fetch articles task.
         */
        const fetchArticlesTask = useTask(function* (signal, shouldEmpty = false) {
            signal.addEventListener('abort', () => duck.abortRequest('fetchArticlesTask'));
            try {
                if (shouldEmpty) {
                    articles.value = [];
                    // isDone.value = false
                }
                const { data } = yield duck.pub.getPubTax({
                    'list[limit]': articlesCards.value,
                    'list[offset]': articles.value.length * (pageOffset.value + 1),
                    'tax[url]': route.value.path.replace(/^\/|\/$/g, ''),
                });
                if (data.articles.length < articlesCards.value) {
                    isDone.value = true;
                }
                if (!data.articles?.length) {
                    isDone.value = true;
                    return;
                }
                articles.value = shouldEmpty ? data.articles : [...articles.value, ...data.articles];
                if (shouldEmpty) {
                    isDone.value = false;
                }
            }
            catch (error) {
                console.error(error);
            }
        });
        function handleGridIntersect(gridKey) {
            gridKey = gridKey + 1 + pageOffset.value;
            const params = new URLSearchParams(location.search);
            if (gridKey > 1) {
                params.set('page', gridKey.toString());
            }
            else {
                params.delete('page');
            }
            history.replaceState(null, '', gridKey > 1 ? `?page=${gridKey}` : context.route.value.fullPath);
        }
        const baseImage = computed(() => {
            const { field_file: svg, field_logo: image, name } = currentPartner.value?.fields || {};
            const attrs = {
                alt: name,
                isBlurhashEnabled: false,
            };
            if (svg?.value) {
                return {
                    ...attrs,
                    image: svg.value,
                    shouldCrop: false,
                    shouldUseWebp: false,
                };
            }
            if (image?.value) {
                return {
                    ...attrs,
                    image: image.value,
                    cropMethod: 'contain',
                };
            }
            return false;
        });
        const searchTask = useTask(function* (signal) {
            signal.addEventListener('abort', () => duck.abortRequest('searchTask'));
            isDone.value = true;
            isFoundArticles.value = true;
            try {
                articles.value = [];
                const { data } = yield duck.pub.getPubArticles({
                    'node[sections]': SECTIONS.club.id,
                    'node[title]': currentFilter.value,
                    ...(currentPartner.value && { 'node[club_clients]': String(currentPartner.value.id) }),
                }, {
                    cancelToken: 'searchTask',
                });
                articles.value = [...articles.value, ...data];
                isDone.value = false;
            }
            catch (error) {
                console.error(error);
            }
            finally {
                isDone.value = true;
            }
        });
        function handleSearch() {
            foundPartner.value = currentFilter.value;
            const params = new URLSearchParams(location.search);
            params.delete('type');
            const query = params.toString();
            history.replaceState(null, '', query.length ? `?${query}` : location.pathname);
            if (foundPartner.value === '') {
                fetchArticlesTask.perform(true);
            }
            else {
                searchTask.perform();
            }
        }
        function handleReset() {
            currentFilter.value = '';
            foundPartner.value = '';
            isDone.value = true;
            if (isFoundArticles.value) {
                fetchArticlesTask.perform(true);
                isFoundArticles.value = false;
            }
        }
        function handleIntersect() {
            if (fetchArticlesTask.isRunning || isDone.value) {
                return;
            }
            !currentFilter.value.length ? fetchArticlesTask.perform() : searchTask.perform();
        }
        return {
            articlesCards,
            css,
            columns,
            currentFilter,
            isSearchFieldHasError,
            fetchArticlesTask,
            searchTask,
            grids,
            handleGridIntersect,
            handleSearch,
            handleReset,
            handleIntersect,
            isDone,
            presets,
            tabs,
            CLUB_PROMO,
            tabsList,
            currentPartner,
            baseImage,
            foundPartner,
        };
    },
    async asyncData(context) {
        const { app, env, error, route } = context;
        try {
            let isDone = false;
            const path = route.path.replace(/^\/|\/$/g, '');
            const { data: partners } = await duck.pub.getPubClubClients();
            const tabsList = [{ name: 'Все', url_alias: 'club/partners' }, ...partners].reduce((tabs, partner) => {
                tabs.push({
                    title: partner.tab_title || partner.name,
                    to: '/' + partner.url_alias,
                    value: partner.url_alias,
                });
                return tabs;
            }, []);
            const currentTab = tabsList.find((tab) => tab.value === path);
            const isAllPartners = currentTab?.title.toLowerCase() === 'все';
            const limit = getArticlesSize(app.$viewport.breakpoint, {
                columns,
                presets,
            });
            const pageNumber = Number(route.query.page) || 0;
            const pageOffset = pageNumber > 0 ? pageNumber - 1 : 0;
            let articles = [];
            const { data } = await duck.pub.getPubTax({
                'list[limit]': limit,
                'list[offset]': limit * pageOffset,
                'tax[url]': path,
            });
            if (data.articles.length < limit) {
                isDone = true;
            }
            articles = data.articles || [];
            const currentPartner = !isAllPartners
                ? {
                    id: data.id,
                    name: data.name,
                    description: data.description,
                    fields: data.fields,
                }
                : null;
            const currentTitle = isAllPartners ? 'Партнеры' : currentTab?.title;
            const title = `${currentTitle} | Forbes Club | Forbes.ru`;
            const head = {
                canonical: env.HOST + route.path,
                title,
                image: '/share-club.png',
                url: env.HOST + route.path,
            };
            context.$page = app.$page = {
                title,
            };
            return {
                $page: context.$page,
                articles,
                head,
                isDone,
                pageOffset,
                title,
                currentPartner,
                tabsList,
                currentTab,
            };
        }
        catch (e) {
            console.error(e);
            error({ statusCode: 404 });
        }
    },
    head() {
        return createHead(this.head, { shouldUseTitleTemplate: false });
    },
});
